import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Suspense, useState } from 'react';
import * as v from 'valibot';
import {
	Area,
	AreaChart,
	CartesianGrid,
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';
import {
	AlertTriangle,
	Calendar,
	CalendarDays,
	Check,
	ChevronDown,
	ChevronRight,
	Coins,
	ExternalLink,
	Heart,
	ListChecks,
	MapPin,
	Percent,
	Receipt,
	TrendingUp,
} from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { Card } from '@worldcoo/ui/components/card';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@worldcoo/ui/components/dropdown-menu';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { cn } from '@worldcoo/ui/lib/utils';
import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import type { Locale } from '@worldcoo/monolith/core';
import { useNgoDashboardQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import {
	CampaignMultiComboboxFilter,
	ChannelFilter,
	DateRangeFilter,
	SingleComboboxFilter,
} from './-components/dashboard-filters';

const CAMPAIGNS_PAGE_SIZE = 6;
const CHANNEL_COLORS: Record<ChannelBucket, string> = {
	App: '#a4c7e3',
	Tpv: '#d9c37e',
	Web: '#a883c1',
};

type Scope = 'all' | 'myPortal' | 'worldcoo';
type ChannelBucket = 'App' | 'Tpv' | 'Web';
type CampaignStatus = 'draft' | 'active' | 'paused' | 'funded';

const sortKeys = ['name', 'revenue', 'funding', 'status', 'startDate', 'endDate'] as const;
type CampaignSortKey = (typeof sortKeys)[number];

const searchSchema = v.object({
	scope: v.fallback(v.optional(v.picklist(['all', 'myPortal', 'worldcoo'])), 'all'),
	dateFrom: v.fallback(v.optional(v.string()), undefined),
	dateTo: v.fallback(v.optional(v.string()), undefined),
	campaignIds: v.fallback(v.optional(v.array(v.string())), undefined),
	merchantId: v.fallback(v.optional(v.string()), undefined),
	channelBucket: v.fallback(v.optional(v.picklist(['App', 'Tpv', 'Web'])), undefined),
	city: v.fallback(v.optional(v.string()), undefined),
	page: v.fallback(v.optional(v.number()), undefined),
	sortKey: v.fallback(v.optional(v.picklist(sortKeys)), undefined),
	sortOrder: v.fallback(v.optional(v.picklist(['asc', 'desc'])), undefined),
});

type SearchParams = v.InferOutput<typeof searchSchema>;

export const Route = createFileRoute('/ngos/dashboard')({
	validateSearch: searchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => {
		context.queryClient.ensureQueryData(trpc.ngo.getDashboard.queryOptions(buildInput(deps)));
	},
	component: RouteComponent,
});

function buildInput(search: SearchParams) {
	const campaignsSort =
		search.sortKey && search.sortOrder ? { key: search.sortKey, order: search.sortOrder } : undefined;
	return {
		scope: search.scope ?? 'all',
		filter: {
			dateFrom: search.dateFrom,
			dateTo: search.dateTo,
			campaignIds: search.campaignIds,
			merchantId: search.merchantId,
			channelBucket: search.channelBucket,
			city: search.city,
		},
		campaignsPage: search.page ?? 1,
		campaignsPageSize: CAMPAIGNS_PAGE_SIZE,
		campaignsSort,
	};
}

function RouteComponent() {
	return (
		<Suspense fallback={<DashboardSkeleton />}>
			<DashboardPage />
		</Suspense>
	);
}

function DashboardPage() {
	const { t, i18n } = useLingui();
	const locale = (i18n.locale || 'en') as Locale;
	const search = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/dashboard' });
	const data = useNgoDashboardQuery(buildInput(search));

	const setScope = (scope: Scope) => {
		navigate({ search: { ...search, scope, page: undefined } });
	};

	const updateFilters = (updates: Partial<SearchParams>) => {
		navigate({ search: { ...search, ...updates, page: undefined } });
	};

	return (
		<>
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Dashboard`}</h1>}
				actions={<ScopeToggle value={search.scope ?? 'all'} onValueChange={setScope} />}
			/>
			<MainSectionContent>
				<div className="flex flex-col gap-6 p-4 md:p-6">
					{!data.dataAvailable && <DataUnavailableBanner />}
					<FiltersRow search={search} filters={data.filters} onChange={updateFilters} />
					<KpiRow kpis={data.kpis} locale={locale} />
					<ChartsRow monthly={data.monthlyEvolution} channels={data.channels} locale={locale} />
					<TopListsRow topCampaigns={data.topCampaigns} topCities={data.topCities} locale={locale} />
					<HighlightsRow highlights={data.highlights} locale={locale} />
					<CampaignsTableCard
						items={data.campaigns.items}
						total={data.campaigns.total}
						page={data.campaigns.page}
						locale={locale}
						sortKey={search.sortKey ?? 'revenue'}
						sortOrder={search.sortOrder ?? 'desc'}
						onSortChange={(key) => {
							// Toggle order when clicking the active column; otherwise pick a sensible
							// default (desc for numeric/date columns, asc for name/status).
							const numericOrDate: CampaignSortKey[] = ['revenue', 'startDate', 'endDate'];
							const defaultOrder = numericOrDate.includes(key) ? 'desc' : 'asc';
							const nextOrder = search.sortKey === key ? (search.sortOrder === 'asc' ? 'desc' : 'asc') : defaultOrder;
							navigate({ search: { ...search, sortKey: key, sortOrder: nextOrder, page: undefined } });
						}}
						onPageChange={(page) => navigate({ search: { ...search, page } })}
					/>
				</div>
			</MainSectionContent>
		</>
	);
}

function ScopeToggle(props: { value: Scope; onValueChange: (value: Scope) => void }) {
	const { t } = useLingui();
	const options: { value: Scope; label: string }[] = [
		{ value: 'all', label: t`All` },
		{ value: 'myPortal', label: t`My Portal` },
		{ value: 'worldcoo', label: t`Worldcoo` },
	];

	return (
		<div className="flex items-center gap-1">
			{options.map((option) => {
				const isActive = props.value === option.value;
				return (
					<button
						key={option.value}
						type="button"
						onClick={() => props.onValueChange(option.value)}
						className={cn(
							'h-9 cursor-pointer rounded-full px-4 text-sm font-medium transition-colors',
							isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
						)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}

function FiltersRow(props: {
	search: SearchParams;
	filters: {
		campaigns: { id: string; name: string | null }[];
		merchants: { id: string; alias: string | null }[];
		cities: string[];
	};
	onChange: (updates: Partial<SearchParams>) => void;
}) {
	const { t } = useLingui();
	const campaignOptions = props.filters.campaigns.map((c) => ({ value: c.id, label: c.name ?? c.id }));
	const merchantOptions = props.filters.merchants.map((m) => ({ value: m.id, label: m.alias ?? m.id }));
	const cityOptions = props.filters.cities.map((city) => ({ value: city, label: city }));

	return (
		<div className="flex flex-wrap items-center gap-3">
			<span className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Filters`}</span>
			<div className="flex flex-wrap items-center gap-2">
				<DateRangeFilter
					dateFrom={props.search.dateFrom}
					dateTo={props.search.dateTo}
					onChange={({ dateFrom, dateTo }) => props.onChange({ dateFrom, dateTo })}
				/>
				<CampaignMultiComboboxFilter
					options={campaignOptions}
					value={props.search.campaignIds ?? []}
					onChange={(ids) => props.onChange({ campaignIds: ids.length > 0 ? ids : undefined })}
				/>
				<SingleComboboxFilter
					label={t`Merchant`}
					searchPlaceholder={t`Search merchants...`}
					emptyMessage={t`No merchants`}
					allLabel={t`All merchants`}
					options={merchantOptions}
					value={props.search.merchantId}
					onChange={(merchantId) => props.onChange({ merchantId })}
				/>
				<ChannelFilter
					value={props.search.channelBucket}
					onChange={(channelBucket) => props.onChange({ channelBucket })}
				/>
				<SingleComboboxFilter
					label={t`City`}
					searchPlaceholder={t`Search cities...`}
					emptyMessage={t`No cities`}
					allLabel={t`All cities`}
					options={cityOptions}
					value={props.search.city}
					onChange={(city) => props.onChange({ city })}
				/>
			</div>
		</div>
	);
}

function DownloadDropdown(props: { campaigns: CampaignRow[] }) {
	const { t } = useLingui();

	const downloadCsv = () => {
		const csv = buildCampaignsCsv(props.campaigns, {
			nameHeader: t`Campaign name`,
			revenueHeader: t`Revenue (EUR cents)`,
			goalHeader: t`Goal (EUR cents)`,
			fundingHeader: t`Funding`,
			statusHeader: t`Status`,
			startDateHeader: t`Start date`,
			endDateHeader: t`End date`,
			portalsHeader: t`Portals`,
		});
		triggerDownload(csv, `campaigns-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8');
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size="lg" className="border-border h-10 rounded-xl px-4 text-sm font-medium">
					{t`Download`}
					<ChevronDown className="size-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem onSelect={downloadCsv}>{t`CSV`}</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function KpiRow(props: {
	kpis: {
		totalAmountCents: number;
		donationsCount: number;
		averageDonationCents: number;
		campaignsCount: number;
	};
	locale: Locale;
}) {
	const { t } = useLingui();

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
			<KpiCard
				label={t`Number of campaigns`}
				value={props.kpis.campaignsCount}
				locale={props.locale}
				decimalDigits={0}
				icon={<ListChecks className="size-6 text-[#1d1d1b]" strokeWidth={1.5} />}
				iconBg="bg-[#e4d9f0]"
			/>
			<KpiCard
				label={t`Total collected`}
				value={props.kpis.totalAmountCents / 100}
				locale={props.locale}
				decimalDigits={2}
				suffix="€"
				icon={<Coins className="size-6 text-[#1d1d1b]" strokeWidth={1.5} />}
				iconBg="bg-[#fbd5b5]"
			/>
			<KpiCard
				label={t`Number of donations`}
				value={props.kpis.donationsCount}
				locale={props.locale}
				decimalDigits={0}
				icon={<Heart className="size-6 text-[#1d1d1b]" strokeWidth={1.5} />}
				iconBg="bg-[#cbe2d5]"
			/>
			<KpiCard
				label={t`Average donation`}
				value={props.kpis.averageDonationCents / 100}
				locale={props.locale}
				decimalDigits={2}
				suffix="€"
				icon={<Percent className="size-6 text-[#1d1d1b]" strokeWidth={1.5} />}
				iconBg="bg-[#f9e4ae]"
			/>
		</div>
	);
}

function KpiCard(props: {
	label: string;
	value: number;
	locale: Locale;
	decimalDigits: number;
	suffix?: string;
	icon: React.ReactNode;
	iconBg: string;
}) {
	return (
		<Card className="flex-row items-center gap-4 p-4 md:gap-5 md:p-5">
			<div className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl md:size-14', props.iconBg)}>
				{props.icon}
			</div>
			<div className="flex min-w-0 flex-col gap-1">
				<span className="text-muted-foreground text-xs font-medium tracking-[0.08em] uppercase">{props.label}</span>
				<AnimateNumber
					locale={props.locale}
					value={props.value}
					decimalDigits={props.decimalDigits}
					suffix={props.suffix}
					className="text-foreground text-2xl font-medium tracking-[-0.5px] md:text-3xl"
				/>
			</div>
		</Card>
	);
}

type MonthlyPoint = { month: string; amountCents: number; donationsCount: number };
type ChannelPoint = { bucket: ChannelBucket; amountCents: number; donationsCount: number };

function ChartsRow(props: { monthly: MonthlyPoint[]; channels: ChannelPoint[]; locale: Locale }) {
	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
			<MonthlyEvolutionCard points={props.monthly} locale={props.locale} />
			<ChannelsCard points={props.channels} locale={props.locale} />
		</div>
	);
}

type EvolutionMetric = 'amount' | 'donations';

function MonthlyEvolutionCard(props: { points: MonthlyPoint[]; locale: Locale }) {
	const { t } = useLingui();
	const [metric, setMetric] = useState<EvolutionMetric>('amount');
	const data = props.points.map((p) => ({
		month: formatShortMonth(p.month, props.locale),
		amount: Math.round(p.amountCents / 100),
		donations: p.donationsCount,
	}));

	const subtitle = metric === 'amount' ? t`Amount collected per month` : t`Donations per month`;
	const yTickFormatter = (value: number) =>
		metric === 'amount' ? `€${value.toLocaleString(props.locale)}` : value.toLocaleString(props.locale);

	return (
		<Card className="gap-4 p-5 lg:col-span-2">
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Monthly evolution`}</h3>
					<p className="text-muted-foreground text-sm">{subtitle}</p>
				</div>
				<EvolutionMetricToggle value={metric} onValueChange={setMetric} />
			</div>

			<div className="h-64 w-full">
				{data.length === 0 ? (
					<EmptyChart message={t`No data for the selected range`} />
				) : (
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
							<defs>
								<linearGradient id="evolutionFill" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#7cc29a" stopOpacity={0.55} />
									<stop offset="100%" stopColor="#7cc29a" stopOpacity={0.05} />
								</linearGradient>
							</defs>
							<CartesianGrid stroke="#eef0ee" vertical={false} />
							<XAxis
								dataKey="month"
								tickLine={false}
								axisLine={false}
								tick={{ fill: '#8a8f8a', fontSize: 11 }}
								interval="preserveStartEnd"
							/>
							<YAxis
								tickLine={false}
								axisLine={false}
								tick={{ fill: '#8a8f8a', fontSize: 11 }}
								width={56}
								tickFormatter={yTickFormatter}
							/>
							<RechartsTooltip
								cursor={{ stroke: '#cfd4cf', strokeWidth: 1 }}
								content={<EvolutionTooltip locale={props.locale} metric={metric} />}
							/>
							<Area
								type="monotone"
								dataKey={metric}
								stroke="#4fa377"
								strokeWidth={2}
								fill="url(#evolutionFill)"
								activeDot={{ r: 4, fill: '#4fa377', stroke: '#ffffff', strokeWidth: 2 }}
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</div>
		</Card>
	);
}

function EvolutionMetricToggle(props: { value: EvolutionMetric; onValueChange: (next: EvolutionMetric) => void }) {
	const { t } = useLingui();
	const options: { value: EvolutionMetric; label: string }[] = [
		{ value: 'amount', label: t`Amount` },
		{ value: 'donations', label: t`Donations` },
	];
	return (
		<div className="inline-flex items-center gap-1">
			{options.map((option) => {
				const isActive = props.value === option.value;
				return (
					<button
						key={option.value}
						type="button"
						onClick={() => props.onValueChange(option.value)}
						className={cn(
							'h-9 cursor-pointer rounded-full px-4 text-sm font-medium transition-colors',
							isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
						)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}

function EvolutionTooltip(props: {
	active?: boolean;
	payload?: Array<{ payload: { month: string; amount: number; donations: number } }>;
	locale: Locale;
	metric: EvolutionMetric;
}) {
	if (!props.active || !props.payload?.length) return null;
	const point = props.payload[0]?.payload;
	if (!point) return null;
	const value =
		props.metric === 'amount'
			? `€${point.amount.toLocaleString(props.locale)}`
			: point.donations.toLocaleString(props.locale);
	return (
		<div className="border-border bg-background flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs shadow-md">
			<span className="text-muted-foreground">{point.month}</span>
			<span className="text-foreground font-medium tabular-nums">{value}</span>
		</div>
	);
}

function ChannelsCard(props: { points: ChannelPoint[]; locale: Locale }) {
	const { t } = useLingui();
	const data = props.points.map((p) => ({
		name: p.bucket,
		value: Math.round(p.amountCents / 100),
		color: CHANNEL_COLORS[p.bucket],
	}));
	const hasData = data.some((d) => d.value > 0);

	return (
		<Card className="gap-4 p-5">
			<div className="flex flex-col gap-1">
				<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Channels`}</h3>
				<p className="text-muted-foreground text-sm">{t`Donations breakdown`}</p>
			</div>

			<div className="relative h-48 w-full">
				{!hasData ? (
					<EmptyChart message={t`No donations yet`} />
				) : (
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								dataKey="value"
								nameKey="name"
								cx="50%"
								cy="50%"
								innerRadius={50}
								outerRadius={80}
								paddingAngle={2}
								stroke="none"
							>
								{data.map((entry) => (
									<Cell key={entry.name} fill={entry.color} />
								))}
							</Pie>
							<RechartsTooltip content={<ChannelTooltip locale={props.locale} />} />
						</PieChart>
					</ResponsiveContainer>
				)}
			</div>

			<div className="flex items-center justify-center gap-4">
				{(['App', 'Tpv', 'Web'] as const).map((bucket) => (
					<div key={bucket} className="flex items-center gap-1.5">
						<span className="size-2 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[bucket] }} />
						<span className="text-muted-foreground text-xs">{bucket === 'Tpv' ? 'TPV' : bucket}</span>
					</div>
				))}
			</div>
		</Card>
	);
}

function ChannelTooltip(props: {
	active?: boolean;
	payload?: Array<{ payload: { name: string; value: number; color: string } }>;
	locale: Locale;
}) {
	if (!props.active || !props.payload?.length) return null;
	const point = props.payload[0]?.payload;
	if (!point) return null;
	return (
		<div className="border-border bg-background flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs shadow-md">
			<span className="size-2 rounded-full" style={{ backgroundColor: point.color }} />
			<span className="text-muted-foreground">{point.name === 'Tpv' ? 'TPV' : point.name}</span>
			<span className="text-foreground font-medium tabular-nums">€{point.value.toLocaleString(props.locale)}</span>
		</div>
	);
}

function TopListsRow(props: {
	topCampaigns: { campaignId: string; name: string; amountCents: number }[];
	topCities: { city: string; amountCents: number; donationsCount: number }[];
	locale: Locale;
}) {
	const { t } = useLingui();

	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<TopBarList
				title={t`Top campaigns`}
				subtitle={t`Total amount collected per campaign`}
				items={props.topCampaigns.map((c) => ({ label: c.name, amountCents: c.amountCents }))}
				emptyMessage={t`No campaigns yet`}
				locale={props.locale}
			/>
			<TopBarList
				title={t`Top cities`}
				subtitle={t`Total amount collected per city`}
				items={props.topCities.map((c) => ({ label: c.city, amountCents: c.amountCents }))}
				emptyMessage={t`No city data yet`}
				locale={props.locale}
			/>
		</div>
	);
}

function TopBarList(props: {
	title: string;
	subtitle: string;
	items: { label: string; amountCents: number }[];
	emptyMessage: string;
	locale: Locale;
}) {
	const maxAmount = Math.max(...props.items.map((item) => item.amountCents), 1);

	return (
		<Card className="gap-4 p-5">
			<div className="flex flex-col gap-1">
				<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{props.title}</h3>
				<p className="text-muted-foreground text-sm">{props.subtitle}</p>
			</div>
			{props.items.length === 0 ? (
				<p className="text-muted-foreground py-6 text-center text-sm">{props.emptyMessage}</p>
			) : (
				<ul className="flex flex-col gap-1.5">
					{props.items.map((item) => {
						const widthPct = Math.max((item.amountCents / maxAmount) * 100, 8);
						return (
							<li key={item.label} className="relative flex h-9 items-center overflow-hidden rounded-md">
								<div className="absolute inset-y-0 left-0 rounded-md bg-[#ffedd5]" style={{ width: `${widthPct}%` }} />
								<div className="relative flex w-full items-center justify-between px-3">
									<span className="text-foreground truncate text-sm">{item.label}</span>
									<span className="text-foreground text-sm tabular-nums">
										{formatEurosCompact(item.amountCents, props.locale)}
									</span>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</Card>
	);
}

function HighlightsRow(props: {
	highlights: {
		topDonation: { amountCents: number; city: string | null } | null;
		bestDay: { date: string; amountCents: number; donationsCount: number } | null;
		mostGenerousCity: { city: string; amountCents: number; donationsCount: number } | null;
		topMerchant: { merchantId: string; alias: string | null; amountCents: number } | null;
		bestMonth: { month: string; amountCents: number } | null;
	};
	locale: Locale;
}) {
	const { t } = useLingui();
	const { topDonation, bestDay, mostGenerousCity, topMerchant, bestMonth } = props.highlights;
	const dash = '—';

	const donationsWord = t`donations`;
	const collectedWord = t`collected`;
	const bestDaySubtitle = bestDay
		? `${bestDay.donationsCount} ${donationsWord} - ${formatEurosRounded(bestDay.amountCents, props.locale)}`
		: '';
	const mostGenerousSubtitle = mostGenerousCity
		? `${formatEurosRounded(mostGenerousCity.amountCents, props.locale)} - ${mostGenerousCity.donationsCount} ${donationsWord}`
		: '';
	const topMerchantSubtitle = topMerchant
		? `${formatEurosRounded(topMerchant.amountCents, props.locale)} ${collectedWord}`
		: '';
	const bestMonthSubtitle = bestMonth
		? `${formatEurosRounded(bestMonth.amountCents, props.locale)} ${collectedWord}`
		: '';

	return (
		<div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
			<HighlightCard
				label={t`Top donation`}
				value={topDonation ? formatEurosRounded(topDonation.amountCents, props.locale) : dash}
				numericValue={topDonation ? Math.round(topDonation.amountCents / 100) : undefined}
				locale={props.locale}
				suffix="€"
				subtitle={topDonation?.city ?? ''}
				icon={<TrendingUp className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Best day`}
				value={bestDay ? formatShortDate(bestDay.date, props.locale) : dash}
				subtitle={bestDaySubtitle}
				icon={<CalendarDays className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Most generous city`}
				value={mostGenerousCity?.city ?? dash}
				subtitle={mostGenerousSubtitle}
				icon={<MapPin className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Top merchant`}
				value={topMerchant?.alias ?? dash}
				subtitle={topMerchantSubtitle}
				icon={<Receipt className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Best month`}
				value={bestMonth ? formatLongMonth(bestMonth.month, props.locale) : dash}
				subtitle={bestMonthSubtitle}
				icon={<Calendar className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
		</div>
	);
}

function HighlightCard(props: {
	label: string;
	value: string;
	subtitle: string;
	icon: React.ReactNode;
	numericValue?: number;
	locale?: Locale;
	suffix?: string;
}) {
	return (
		<Card className="items-center gap-2 p-4 text-center md:p-5">
			<div className="flex size-10 items-center justify-center rounded-xl bg-[#e4d9f0]">{props.icon}</div>
			<span className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">{props.label}</span>
			{props.numericValue != null && props.locale ? (
				<AnimateNumber
					locale={props.locale}
					value={props.numericValue}
					decimalDigits={0}
					suffix={props.suffix}
					className="text-foreground text-2xl font-medium tracking-[-0.3px]"
				/>
			) : (
				<span className="text-foreground text-2xl font-medium tracking-[-0.3px] tabular-nums">{props.value}</span>
			)}
			<span className="text-muted-foreground text-xs">{props.subtitle || ' '}</span>
		</Card>
	);
}

type CampaignRow = {
	id: string;
	name: string;
	revenueCents: number;
	goalCents: number | null;
	funding: ('myPortal' | 'worldcoo')[];
	status: CampaignStatus;
	startDate: string | null;
	endDate: string | null;
	portals: PortalLink[];
};

type PortalLink = { portalId: string; label: string; url: string; accentColor: string | null };

function CampaignsTableCard(props: {
	items: CampaignRow[];
	total: number;
	page: number;
	locale: Locale;
	sortKey: CampaignSortKey;
	sortOrder: 'asc' | 'desc';
	onSortChange: (key: CampaignSortKey) => void;
	onPageChange: (page: number) => void;
}) {
	const { t } = useLingui();
	const totalPages = Math.max(1, Math.ceil(props.total / CAMPAIGNS_PAGE_SIZE));

	const headerProps = (key: CampaignSortKey) => ({
		isSorted: props.sortKey === key,
		sortOrder: props.sortOrder,
		onSort: () => props.onSortChange(key),
	});

	return (
		<Card className="gap-0 p-0">
			<div className="border-border flex items-center justify-between gap-3 border-b px-5 py-4">
				<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Campaigns`}</h3>
				{props.items.length > 0 && <DownloadDropdown campaigns={props.items} />}
			</div>

			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-border border-b">
							<TableHeaderCell label={t`Campaign name`} {...headerProps('name')} />
							<TableHeaderCell label={t`Revenue`} {...headerProps('revenue')} />
							<TableHeaderCell label={t`Funding`} {...headerProps('funding')} />
							<TableHeaderCell label={t`Start date`} {...headerProps('startDate')} />
							<TableHeaderCell label={t`End date`} {...headerProps('endDate')} />
							<TableHeaderCell label={t`Portals`} />
							<TableHeaderCell label={t`Status`} {...headerProps('status')} />
						</tr>
					</thead>
					<tbody>
						{props.items.length === 0 ? (
							<tr>
								<td colSpan={7} className="text-muted-foreground px-5 py-8 text-center text-sm">
									{t`No campaigns yet`}
								</td>
							</tr>
						) : (
							props.items.map((campaign, idx) => (
								<tr
									key={campaign.id}
									className={cn('border-border/60 border-b transition-colors', idx % 2 === 1 && 'bg-[#fff7ed]/60')}
								>
									<td className="px-5 py-3.5">
										<span className="text-foreground">{campaign.name}</span>
									</td>
									<td className="px-5 py-3.5">
										<div className="flex items-baseline gap-1.5 whitespace-nowrap">
											<span className="text-foreground tabular-nums">
												{formatEurosCompact(campaign.revenueCents, props.locale)}
											</span>
											{campaign.goalCents != null && (
												<span className="text-muted-foreground text-xs tabular-nums">
													{t`of`} {formatEurosCompact(campaign.goalCents, props.locale)}
												</span>
											)}
										</div>
									</td>
									<td className="px-5 py-3.5">
										<div className="flex items-center gap-2">
											{campaign.funding.length === 0 ? (
												<span className="text-muted-foreground text-xs">{t`None`}</span>
											) : (
												campaign.funding.map((f) => <FundingChip key={f} funding={f} />)
											)}
										</div>
									</td>
									<td className="px-5 py-3.5 whitespace-nowrap">
										<DateCell iso={campaign.startDate} locale={props.locale} />
									</td>
									<td className="px-5 py-3.5 whitespace-nowrap">
										<DateCell iso={campaign.endDate} locale={props.locale} />
									</td>
									<td className="px-5 py-3.5">
										<PortalLinks portals={campaign.portals} />
									</td>
									<td className="px-5 py-3.5">
										<StatusBadge status={campaign.status} />
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="flex flex-col items-start gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
				<CampaignCountLine shown={props.items.length} total={props.total} />
				{totalPages > 1 && <Pagination page={props.page} totalPages={totalPages} onPageChange={props.onPageChange} />}
			</div>
		</Card>
	);
}

function CampaignCountLine(props: { shown: number; total: number }) {
	const { t } = useLingui();
	const shown = props.shown;
	const total = props.total;
	return <span className="text-muted-foreground text-sm">{t`Showing ${shown} of ${total} campaigns`}</span>;
}

function DateCell(props: { iso: string | null; locale: Locale }) {
	if (!props.iso) return <span className="text-muted-foreground text-xs">—</span>;
	return <span className="text-foreground tabular-nums">{formatIsoDate(props.iso, props.locale)}</span>;
}

function PortalLinks(props: { portals: PortalLink[] }) {
	const { t } = useLingui();
	if (props.portals.length === 0) {
		return <span className="text-muted-foreground text-xs">{t`None`}</span>;
	}
	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{props.portals.map((portal) => {
				// When an accent color is provided, use it as border + text tint; otherwise fall
				// back to the neutral border variant.
				const style = portal.accentColor ? { color: portal.accentColor } : undefined;
				return (
					<a
						key={portal.portalId}
						href={portal.url}
						target="_blank"
						rel="noopener noreferrer"
						style={style}
						className={cn(
							'inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-medium transition-colors bg-[color-mix(in_oklch,currentColor_5%,transparent)] hover:bg-[color-mix(in_oklch,currentColor_10%,transparent)]',
							!portal.accentColor && 'bg-muted/80 text-foreground hover:bg-muted',
						)}
					>
						{portal.label}
						<ExternalLink className="size-3" />
					</a>
				);
			})}
		</div>
	);
}

function TableHeaderCell(props: {
	label: string;
	isSorted?: boolean;
	sortOrder?: 'asc' | 'desc';
	onSort?: () => void;
}) {
	const sortable = !!props.onSort;
	const ariaSort: 'ascending' | 'descending' | 'none' | undefined = !sortable
		? undefined
		: props.isSorted
			? props.sortOrder === 'asc'
				? 'ascending'
				: 'descending'
			: 'none';
	return (
		<th className="px-5 py-3 text-left" aria-sort={ariaSort}>
			<button
				type="button"
				onClick={props.onSort}
				disabled={!sortable}
				className={cn(
					'inline-flex items-center gap-1 text-xs font-medium tracking-[0.08em] uppercase',
					sortable ? 'hover:text-foreground cursor-pointer' : 'cursor-default',
					props.isSorted ? 'text-foreground' : 'text-muted-foreground',
				)}
			>
				{props.label}
				{sortable && (
					<ChevronDown
						className={cn(
							'size-3.5 transition-transform',
							props.isSorted && props.sortOrder === 'asc' && 'rotate-180',
							!props.isSorted && 'opacity-50',
						)}
					/>
				)}
			</button>
		</th>
	);
}

function FundingChip(props: { funding: 'myPortal' | 'worldcoo' }) {
	const { t } = useLingui();
	const label = props.funding === 'myPortal' ? t`My Portal` : t`Worldcoo`;
	return (
		<span className="border-border text-foreground inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium">
			{label}
		</span>
	);
}

function StatusBadge(props: { status: CampaignStatus }) {
	const { t } = useLingui();
	switch (props.status) {
		case 'funded':
			return (
				<span className="inline-flex h-6 items-center gap-1 rounded-full bg-[#d1f0dc] px-2.5 text-xs font-medium text-[#1f6e3d]">
					<Check className="size-3.5" strokeWidth={2.5} />
					{t`Funded`}
				</span>
			);
		case 'active':
			return (
				<span className="inline-flex h-6 items-center rounded-full bg-[#eaf4ed] px-3 text-xs font-medium text-[#1f6e3d]">
					{t`Active`}
				</span>
			);
		case 'paused':
			return (
				<span className="inline-flex h-6 items-center rounded-full bg-[#fef3e4] px-3 text-xs font-medium text-[#8a5a1a]">
					{t`Paused`}
				</span>
			);
		case 'draft':
			return (
				<span className="inline-flex h-6 items-center rounded-full bg-[#f1f2f1] px-3 text-xs font-medium text-[#5a605a]">
					{t`Draft`}
				</span>
			);
	}
}

function Pagination(props: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
	const { t } = useLingui();
	const pages = Array.from({ length: props.totalPages }, (_, i) => i + 1);

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{pages.map((pageNum) => (
				<button
					key={pageNum}
					type="button"
					onClick={() => props.onPageChange(pageNum)}
					className={cn(
						'h-8 w-8 cursor-pointer rounded-md border text-sm font-medium transition-colors',
						pageNum === props.page
							? 'bg-primary text-primary-foreground border-primary'
							: 'border-border text-foreground hover:bg-muted',
					)}
				>
					{pageNum}
				</button>
			))}
			<button
				type="button"
				onClick={() => props.onPageChange(Math.min(props.page + 1, props.totalPages))}
				disabled={props.page >= props.totalPages}
				className="border-border text-foreground hover:bg-muted inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border px-3 text-sm font-medium transition-colors disabled:cursor-default disabled:opacity-50"
			>
				{t`Next`}
				<ChevronRight className="size-4" />
			</button>
		</div>
	);
}

function DataUnavailableBanner() {
	const { t } = useLingui();
	return (
		<div
			role="alert"
			className="border-portal-border flex items-start gap-3 rounded-xl border bg-[#fef3e4] p-4 text-[#8a5a1a]"
		>
			<AlertTriangle className="mt-0.5 size-5 shrink-0" strokeWidth={1.8} />
			<div className="flex flex-col gap-0.5">
				<p className="text-sm font-medium">{t`Some data couldn't be loaded right now.`}</p>
				<p className="text-xs opacity-80">
					{t`We're showing what's available. Refresh in a few minutes or contact support if the problem persists.`}
				</p>
			</div>
		</div>
	);
}

function EmptyChart(props: { message: string }) {
	return <div className="text-muted-foreground flex h-full items-center justify-center text-sm">{props.message}</div>;
}

function DashboardSkeleton() {
	return (
		<>
			<div className="border-portal-border flex min-h-24 shrink-0 items-center justify-between border-b bg-white px-4 py-4 md:px-6">
				<Skeleton className="h-8 w-32" />
				<Skeleton className="h-9 w-64 rounded-full" />
			</div>
			<div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6">
				<Skeleton className="h-10 w-full rounded-xl" />
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						// oxlint-disable-next-line react/no-array-index-key
						<Skeleton key={`kpi-${i}`} className="h-24 rounded-xl" />
					))}
				</div>
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
					<Skeleton className="h-72 rounded-xl lg:col-span-2" />
					<Skeleton className="h-72 rounded-xl" />
				</div>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<Skeleton className="h-56 rounded-xl" />
					<Skeleton className="h-56 rounded-xl" />
				</div>
				<div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
					{Array.from({ length: 5 }).map((_, i) => (
						// oxlint-disable-next-line react/no-array-index-key
						<Skeleton key={`hl-${i}`} className="h-40 rounded-xl" />
					))}
				</div>
				<Skeleton className="h-96 rounded-xl" />
			</div>
		</>
	);
}

// ── Formatting helpers ──────────────────────────────────────────────────────

function formatEurosRounded(cents: number, locale: Locale): string {
	const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(cents / 100));
	return `€ ${formatted}`;
}

function formatEurosCompact(cents: number, locale: Locale): string {
	const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(cents / 100));
	return `€${formatted}`;
}

function formatShortMonth(yyyymmdd: string, locale: Locale): string {
	// "2025-04-01" → "abr 25" (Spanish) / "Apr 25" (English)
	const [yearStr, monthStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
	const month = date.toLocaleDateString(locale, { month: 'short' });
	return `${month} ${yearStr.slice(2)}`;
}

function formatLongMonth(yyyymmdd: string, locale: Locale): string {
	// "2025-01-01" → "enero" (Spanish) / "January" (English)
	const [yearStr, monthStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
	return date.toLocaleDateString(locale, { month: 'long' });
}

function formatShortDate(yyyymmdd: string, locale: Locale): string {
	// "2025-03-23" → "23 mar" (Spanish) / "Mar 23" (English)
	const [yearStr, monthStr, dayStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr || !dayStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
	return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

function formatIsoDate(iso: string, locale: Locale): string {
	const [yearStr, monthStr, dayStr] = iso.split('-');
	if (!yearStr || !monthStr || !dayStr) return iso;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
	return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── CSV download ────────────────────────────────────────────────────────────

type CsvHeaders = {
	nameHeader: string;
	revenueHeader: string;
	goalHeader: string;
	fundingHeader: string;
	statusHeader: string;
	startDateHeader: string;
	endDateHeader: string;
	portalsHeader: string;
};

function escapeCsvCell(value: string): string {
	// Defuse CSV formula injection: cells whose first char is a spreadsheet formula trigger
	// (`=`, `+`, `@`, `-`, tab, CR) are prefixed with a single quote so Excel/Sheets treats
	// them as literal text. See OWASP "CSV Injection".
	const injectionTriggers = /^[=+\-@\t\r]/;
	const safe = injectionTriggers.test(value) ? `'${value}` : value;
	if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
		return `"${safe.replace(/"/g, '""')}"`;
	}
	return safe;
}

function buildCampaignsCsv(campaigns: CampaignRow[], headers: CsvHeaders): string {
	const head = [
		headers.nameHeader,
		headers.revenueHeader,
		headers.goalHeader,
		headers.fundingHeader,
		headers.statusHeader,
		headers.startDateHeader,
		headers.endDateHeader,
		headers.portalsHeader,
	].map(escapeCsvCell);
	const rows = campaigns.map((c) =>
		[
			c.name,
			String(c.revenueCents),
			c.goalCents != null ? String(c.goalCents) : '',
			c.funding.join('; '),
			c.status,
			c.startDate ?? '',
			c.endDate ?? '',
			c.portals.map((p) => `${p.label} (${p.url})`).join('; '),
		].map(escapeCsvCell),
	);
	return [head, ...rows].map((row) => row.join(',')).join('\n');
}

function triggerDownload(content: string, filename: string, mimeType: string) {
	// Prepend UTF-8 BOM so Excel opens the CSV with the correct encoding.
	const blob = new Blob(['\ufeff', content], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}
